import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import ListSubheader from "@mui/material/ListSubheader"
import Paper from "@mui/material/Paper"
import Stack from "@mui/material/Stack"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import { useGetSstv2EventDiagramsQuery } from "src/features/partners/partnersApi"
import { MermaidDiagram } from "./MermaidDiagram"

type DiagramEntry = {
  eventType: string
  label: string
  category: "locker" | "reservation" | "door"
  description: string
  diagram: string
}

const categoryColors: Record<string, "primary" | "success" | "warning"> = {
  locker: "primary",
  reservation: "success",
  door: "warning",
}

const categoryLabels: Record<string, string> = {
  locker: "Locker Events",
  reservation: "Reservation Events",
  door: "Door Events",
}

export const Sstv2EventFlowTab: FC = () => {
  const { data, isLoading, error } = useGetSstv2EventDiagramsQuery()
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const diagrams = data?.data ?? []

  const byCategory = useMemo(
    () => ({
      locker: diagrams.filter((d) => d.category === "locker"),
      reservation: diagrams.filter((d) => d.category === "reservation"),
      door: diagrams.filter((d) => d.category === "door"),
    }),
    [diagrams],
  )

  // Auto-select first reservation event once data loads
  const selected: DiagramEntry | null = useMemo(() => {
    if (!diagrams.length) return null
    if (selectedType) return diagrams.find((d) => d.eventType === selectedType) ?? diagrams[0]
    return byCategory.reservation[0] ?? diagrams[0]
  }, [diagrams, selectedType, byCategory])

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true))
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false))
    }
  }, [])

  const handleFullscreenChange = useCallback(() => {
    setIsFullscreen(!!document.fullscreenElement)
  }, [])

  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Stack>
    )
  }

  if (error || !diagrams.length) {
    return (
      <Typography color="error" variant="body2">
        Failed to load event diagrams.
      </Typography>
    )
  }

  return (
    <Stack data-ui="UI-SL2W-243"
      ref={containerRef}
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      onFullscreenChange={handleFullscreenChange}
      sx={
        isFullscreen
          ? { bgcolor: "background.default", p: 2, overflow: "auto" }
          : undefined
      }
    >
      {/* Left sidebar — event selector */}
      <Paper
        variant="outlined"
        sx={{
          width: { xs: "100%", md: 280 },
          flexShrink: 0,
          overflow: "auto",
          maxHeight: isFullscreen ? "100vh" : { md: "75vh" },
        }}
      >
        <List dense disablePadding>
          {(["locker", "reservation", "door"] as const).map((cat) => (
            <Box key={cat}>
              <ListSubheader
                sx={{
                  bgcolor: "background.paper",
                  lineHeight: "36px",
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                {categoryLabels[cat]}
              </ListSubheader>
              {byCategory[cat].map((entry) => (
                <ListItemButton
                  key={entry.eventType}
                  selected={selected?.eventType === entry.eventType}
                  onClick={() => setSelectedType(entry.eventType)}
                  sx={{ py: 0.75 }}
                >
                  <ListItemText
                    primary={entry.label}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight:
                        selected?.eventType === entry.eventType ? 600 : 400,
                    }}
                    secondary={entry.eventType}
                    secondaryTypographyProps={{
                      variant: "caption",
                      fontFamily: "monospace",
                    }}
                  />
                </ListItemButton>
              ))}
            </Box>
          ))}
        </List>
      </Paper>

      {/* Right content — diagram */}
      {selected && (
        <Paper variant="outlined" sx={{ flex: 1, p: 3, overflow: "auto" }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              <AccountTreeOutlinedIcon color="action" />
              <Typography variant="h6" fontWeight={600}>
                {selected.label}
              </Typography>
              <Chip
                label={selected.eventType}
                size="small"
                color={categoryColors[selected.category]}
                variant="outlined"
                sx={{ fontFamily: "monospace" }}
              />
              <Box sx={{ flex: 1 }} />
              <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                <IconButton onClick={toggleFullscreen} size="small">
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {selected.description}
            </Typography>

            <Divider />

            <MermaidDiagram chart={selected.diagram} />
          </Stack>
        </Paper>
      )}
    </Stack>
  )
}
